import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import type { MinimalUser } from '@pbk-web/types';
import type { LexicalEditor, NodeKey } from 'lexical';
import {
  $createTextNode,
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  KEY_DOWN_COMMAND,
  KEY_ESCAPE_COMMAND,
} from 'lexical';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useEditorChrome } from '../../core/context/EditorChromeContext';
import { useFeatureConfig } from '../../core/context/FeaturesContext';
import { EditorPortal } from '../../ui/EditorPortal';
import { panelClasses } from '../../ui/styles';
import { characterRect } from '../../utils/characterRect';
import { registerAtomicDeletion } from '../../utils/registerAtomicDeletion';
import { $createMentionNode, $isMentionNode, type MentionUser } from './MentionNode';
import { MentionsMenu } from './MentionsMenu';
import { registerMentionTracking } from './registerMentionTracking';
import { mentionTokens, registerMentionTransform } from './registerMentionTransform';

const TRIGGER = '@';
const NO_USERS: MinimalUser[] = [];

/** Where the `@` that opened the menu is: its text node and offset. */
type Trigger = { key: NodeKey; offset: number };

/**
 * Mentions: `@` at the start of a word opens a user search; picking one replaces the `@` with a `MentionNode`.
 * - The menu takes the keyboard after a moment (see `MentionsMenu`); until then, typing on closes it.
 * - Also mounts turning saved text back into mentions, whole-node deletion and the added/removed reporting.
 */
export function MentionsPlugin() {
  const [editor] = useLexicalComposerContext();
  const config = useFeatureConfig('mentions');
  const { focused } = useEditorChrome();
  const users = config?.users ?? NO_USERS;
  const serialise = config?.serialise;
  const [trigger, setTrigger] = useState<Trigger | null>(null);
  const close = useCallback(() => setTrigger(null), []);

  // The latest options, for listeners registered once
  const configRef = useRef(config);
  configRef.current = config;

  // Hosts often pass a new `users` array or `serialise` function each render. Rebuilding the transform re-marks
  // every text node (an update, reported through `onChange`, re-rendering the host), so it only follows the tokens.
  const signature = serialise
    ? users.map((user) => `${user.id}\u0000${user.firstName}\u0000${user.lastName}\u0000${serialise(user)}`).join('\u0001')
    : '';
  const tokensRef = useRef<{ signature: string; tokens: ReturnType<typeof mentionTokens> } | null>(null);
  if (tokensRef.current?.signature !== signature) {
    tokensRef.current = { signature, tokens: serialise ? mentionTokens(users, serialise) : new Map() };
  }
  const tokens = tokensRef.current.tokens;

  useEffect(() => registerMentionTransform(editor, tokens), [editor, tokens]);
  useEffect(() => registerAtomicDeletion(editor, $isMentionNode), [editor]);
  useEffect(() => {
    // The host's own user object when it's still listed; otherwise the copy the mention kept
    const resolve = (user: MentionUser): MinimalUser => configRef.current?.users.find(({ id }) => id === user.id) ?? user;
    return registerMentionTracking(editor, {
      onAdded: (user) => configRef.current?.onAdded?.(resolve(user)),
      onRemoved: (user) => configRef.current?.onRemoved?.(resolve(user)),
    });
  }, [editor]);

  // Open on `@` once it's in the document, and only at the start of a word, so an email address doesn't open it
  useEffect(
    () =>
      editor.registerCommand(
        KEY_DOWN_COMMAND,
        (event) => {
          if (event.key !== TRIGGER) return false;
          setTimeout(() => {
            editor.getEditorState().read(() => {
              const selection = $getSelection();
              if (!$isRangeSelection(selection) || !selection.isCollapsed()) return;
              const node = selection.anchor.getNode();
              if (!$isTextNode(node)) return;
              const text = node.getTextContent();
              const at = selection.anchor.offset - 1;
              if (text[at] !== TRIGGER || (at > 0 && !/\s/.test(text[at - 1]))) return;
              setTrigger({ key: node.getKey(), offset: at });
            });
          }, 0);
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    [editor],
  );

  // Close once the caret leaves the `@` (typing on, a click elsewhere in the text) or Escape is pressed in the editor
  useEffect(() => {
    if (!trigger) return;
    const unregisterUpdate = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        // No selection is the menu's search having the keyboard: the caret is still where it was
        if (selection === null) return;
        const stillAtTrigger =
          $isRangeSelection(selection) &&
          selection.isCollapsed() &&
          selection.anchor.key === trigger.key &&
          selection.anchor.offset === trigger.offset + 1;
        if (!stillAtTrigger) close();
      });
    });
    const unregisterEscape = editor.registerCommand(
      KEY_ESCAPE_COMMAND,
      () => {
        close();
        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );
    return () => {
      unregisterUpdate();
      unregisterEscape();
    };
  }, [editor, trigger, close]);

  // Deactivating the editor (a click outside it, tabbing away) closes it too
  useEffect(() => {
    if (!focused) close();
  }, [focused, close]);

  const pick = useCallback(
    (user: MinimalUser) => {
      if (!trigger || !serialise) return;
      close();
      editor.update(
        () => {
          const node = $getNodeByKey(trigger.key);
          // The `@` may have gone in the meantime (e.g. a collaborator's edit)
          if (!$isTextNode(node) || node.getTextContent()[trigger.offset] !== TRIGGER) return;
          const space = $createTextNode(' ');
          node.select(trigger.offset, trigger.offset + 1).insertNodes([$createMentionNode(user, serialise(user)), space]);
          space.select(1, 1);
        },
        // Focused once committed: `editor.focus()` skips the DOM focus while an update is pending
        { onUpdate: () => editor.focus() },
      );
    },
    [editor, trigger, serialise, close],
  );

  const cancel = useCallback(() => {
    close();
    editor.focus();
  }, [editor, close]);

  return trigger && <MentionsPopover editor={editor} trigger={trigger} users={users} onPick={pick} onCancel={cancel} />;
}

type PopoverProps = {
  editor: LexicalEditor;
  trigger: Trigger;
  users: readonly MinimalUser[];
  onPick: (user: MinimalUser) => void;
  onCancel: () => void;
};

/** The menu, placed below the `@` and following it as the page scrolls or the text reflows. */
const MentionsPopover = ({ editor, trigger, users, onPick, onCancel }: PopoverProps) => {
  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    strategy: 'fixed',
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const reference = useMemo(
    () => ({ getBoundingClientRect: () => characterRect(editor, trigger.key, trigger.offset), contextElement: editor.getRootElement() ?? undefined }),
    [editor, trigger],
  );
  useLayoutEffect(() => refs.setPositionReference(reference), [refs, reference]);

  return (
    // Not `keepSelection`: the search needs the mouse-down to take focus
    <EditorPortal keepSelection={false} ref={refs.setFloating} style={floatingStyles} className={panelClasses}>
      <MentionsMenu users={users} onPick={onPick} onCancel={onCancel} />
    </EditorPortal>
  );
};
